import { Component, inject, signal, DestroyRef } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TopbarComponent } from './shell/topbar/topbar.component';
import { SidebarComponent } from './shell/sidebar/sidebar.component';
import { ProfileFlyoutComponent } from './shared/profiles/containers/profile-flyout/profile-flyout.component';
import { BrokerProfileFlyoutComponent } from './features/calculator-v2/shared/broker-profile-flyout/broker-profile-flyout.component';
import { CaseBarComponent } from './shell/case-bar/case-bar.component';
import { TopbarV3Component } from './shell/topbar-v3/topbar-v3.component';
import { NavV3Component } from './shell/nav-v3/nav-v3.component';
import { RailHostComponent } from './shell/rail-host/rail-host.component';
import { RailLayoutService } from './shell/rail-host/rail-layout.service';
import { SelectionTrayComponent } from './shell/selection-tray/selection-tray.component';
import { GlobalToastComponent } from './shared/global-toast/global-toast.component';
import { SessionContextService } from './core/services/session-context.service';
import { ProfileService } from './shared/profiles/services/profile.service';
import { ImpersonationAuditService } from './core/services/impersonation-audit.service';
import { UsageTrackerService } from './core/services/usage-tracker.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    TopbarComponent,
    SidebarComponent,
    ProfileFlyoutComponent,
    BrokerProfileFlyoutComponent,
    CaseBarComponent,
    TopbarV3Component,
    NavV3Component,
    RailHostComponent,
    SelectionTrayComponent,
    GlobalToastComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  protected session = inject(SessionContextService);
  protected profile = inject(ProfileService);
  /** Bound on .rail-col, not inside RailHost: the BASKET tray is a sibling of
   *  the rail host in that column and has to collapse with it. */
  protected railLayout = inject(RailLayoutService);
  /** Started here, not as an app initializer: it reads the JWT identity, and
   *  initializers run before a fresh tab has adopted the session. */
  private readonly impersonationAudit = inject(ImpersonationAuditService);
  /** Page visits and profile opens — must be listening before the first
   *  navigation completes, which it is from here. */
  private readonly usageTracker = inject(UsageTrackerService);

  isAdmin = signal(false);
  isAuthRoute = signal(false);
  isHeadlessRoute = signal(new URLSearchParams(window.location.search).get('prerender') === '1');
  showRail = signal(true);

  constructor() {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((e: NavigationEnd) => {
      const url = e.urlAfterRedirects;
      this.isAdmin.set(url.startsWith('/admin'));
      this.isAuthRoute.set(url === '/login' || url.startsWith('/login?') || url.startsWith('/auth/'));
      this.isHeadlessRoute.set(url.includes('prerender=1'));
      this.showRail.set(
        !url.startsWith('/sales') &&
        !url.startsWith('/admin')
      );
    });
  }
}
